import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { servicesData } from '../mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services', hasDropdown: true },
    { name: 'Why AI', path: '/why-ai' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-gray-100'
          : 'bg-white/60 backdrop-blur-md'
      }`}
      data-testid="main-header"
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <motion.img
              src="/logo.png"
              alt="Hei Web Creations — premium web design Singapore"
              className="h-11 w-auto"
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.3 }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-10">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <Link
                    to={link.path}
                    className="relative group flex items-center"
                    data-testid="nav-link-services"
                  >
                    <span
                      className={`text-sm font-medium transition-colors duration-300 ${
                        location.pathname.startsWith('/services')
                          ? 'text-[#C8102E]'
                          : 'text-gray-600 hover:text-[#1A1A1A]'
                      }`}
                    >
                      {link.name}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`ml-1 text-gray-400 transition-transform duration-300 ${
                        isServicesOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </Link>

                  <AnimatePresence>
                    {isServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-72"
                        data-testid="services-dropdown"
                      >
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-2 overflow-hidden">
                          {servicesData.map((service) => (
                            <Link
                              key={service.id}
                              to={`/services/${service.slug}`}
                              className={`block px-4 py-3 rounded-xl text-sm transition-colors duration-200 ${
                                location.pathname === `/services/${service.slug}`
                                  ? 'bg-[#FAFAFA] text-[#C8102E]'
                                  : 'text-gray-600 hover:bg-[#FAFAFA] hover:text-[#1A1A1A]'
                              }`}
                              data-testid={`dropdown-service-${service.slug}`}
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className="relative group"
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  <span
                    className={`text-sm font-medium transition-colors duration-300 ${
                      location.pathname === link.path
                        ? 'text-[#C8102E]'
                        : 'text-gray-600 hover:text-[#1A1A1A]'
                    }`}
                  >
                    {link.name}
                  </span>
                  <motion.span
                    className="absolute -bottom-1 left-0 h-0.5 bg-[#C8102E]"
                    initial={{ width: 0 }}
                    whileHover={{ width: '100%' }}
                    transition={{ duration: 0.3 }}
                  />
                </Link>
              )
            )}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button
                className="bg-[#1A1A1A] hover:bg-[#C8102E] text-white px-6 py-2 rounded-full transition-all duration-300 hover:shadow-lg"
                data-testid="header-cta-button"
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-700"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="container mx-auto px-6 py-6 flex flex-col space-y-2">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {link.hasDropdown ? (
                    <div>
                      <div className="flex items-center justify-between">
                        <Link
                          to={link.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block py-2 text-lg font-medium transition-colors ${
                            location.pathname === link.path
                              ? 'text-[#C8102E]'
                              : 'text-gray-700 hover:text-[#C8102E]'
                          }`}
                          data-testid="mobile-nav-link-services"
                        >
                          {link.name}
                        </Link>
                        <button
                          onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                          className="p-2 text-gray-500"
                          data-testid="mobile-services-toggle"
                          aria-label="Toggle services"
                        >
                          <ChevronDown
                            size={18}
                            className={`transition-transform duration-300 ${
                              isMobileServicesOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      </div>
                      <AnimatePresence>
                        {isMobileServicesOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden pl-4 border-l-2 border-gray-100 ml-1"
                          >
                            {servicesData.map((service) => (
                              <Link
                                key={service.id}
                                to={`/services/${service.slug}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 text-base text-gray-500 hover:text-[#C8102E] transition-colors"
                                data-testid={`mobile-dropdown-service-${service.slug}`}
                              >
                                {service.title}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block py-2 text-lg font-medium transition-colors ${
                        location.pathname === link.path
                          ? 'text-[#C8102E]'
                          : 'text-gray-700 hover:text-[#C8102E]'
                      }`}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </motion.div>
              ))}
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="pt-2">
                <Button className="w-full bg-[#1A1A1A] hover:bg-[#C8102E] text-white py-3 rounded-full">
                  Get Started
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
